import { readOnboardingMode, type OnboardingMode } from './entry'

export const allOnboardingSteps = [
  { key: 'company', label: 'Unternehmen', path: '/app/onboarding/company', subtitle: 'Grundlagen' },
  { key: 'vision', label: 'Kernaussage', path: '/app/onboarding/vision', subtitle: 'Leitbild' },
  { key: 'goals', label: 'Ziele', path: '/app/onboarding/goals', subtitle: '12-Monats-Ziele' },
  { key: 'audience', label: 'Kunden', path: '/app/onboarding/audience', subtitle: 'Deine Zielgruppe' },
  { key: 'market', label: 'Markt & Wettbewerb', path: '/app/onboarding/market', subtitle: 'Euer Umfeld' },
  { key: 'strategy', label: 'Strategie', path: '/app/onboarding/strategy', subtitle: 'Positionierung' },
  { key: 'strategy-proof', label: 'USP & Proof', path: '/app/onboarding/strategy-proof', subtitle: 'Abgrenzung' },
  { key: 'tone', label: 'Tonalitaet', path: '/app/onboarding/tone', subtitle: 'Wie du klingst' },
  { key: 'channels', label: 'Kanaele', path: '/app/onboarding/channels', subtitle: 'Welche Kanaele' },
  { key: 'approval', label: 'Freigabe & Tage', path: '/app/onboarding/approval', subtitle: 'Freigabe & Timing' },
  { key: 'newsletter', label: 'Newsletter', path: '/app/onboarding/newsletter', subtitle: 'Empfaenger' },
  { key: 'design', label: 'Design', path: '/app/onboarding/design', subtitle: 'Visuelles' },
  { key: 'review', label: 'Fertig', path: '/app/onboarding/review', subtitle: 'Finaler Check' },
] as const

export type OnboardingStepKey = (typeof allOnboardingSteps)[number]['key']
export type OnboardingStep = (typeof allOnboardingSteps)[number]

const stepsByMode: Record<OnboardingMode, OnboardingStepKey[]> = {
  express: ['company', 'tone', 'channels', 'review'],
  guided: ['company', 'vision', 'goals', 'audience', 'tone', 'market', 'channels', 'approval', 'newsletter', 'design', 'review'],
  strategy: ['company', 'vision', 'goals', 'audience', 'market', 'strategy', 'strategy-proof', 'tone', 'channels', 'approval', 'newsletter', 'design', 'review'],
}

const labelsByMode: Partial<Record<OnboardingMode, Partial<Record<OnboardingStepKey, string>>>> = {
  express: {
    company: 'Firma',
    tone: 'Ton',
    channels: 'Kanaele',
    review: 'Startklar',
  },
  strategy: {
    strategy: 'Markenkern',
    'strategy-proof': 'USP & Proof',
    review: 'Fundament',
  },
}

const subtitlesByMode: Partial<Record<OnboardingMode, Partial<Record<OnboardingStepKey, string>>>> = {
  express: {
    company: 'Fast Track',
    tone: 'Sprachgefuehl',
    channels: 'Ausspielung',
    review: 'Los gehts',
  },
  strategy: {
    strategy: 'Markenkern',
    'strategy-proof': 'USP & Beweise',
    review: 'Strategischer Abschluss',
  },
}

export function getCurrentOnboardingMode() {
  return readOnboardingMode() ?? 'guided'
}

export function getOnboardingSteps(mode = getCurrentOnboardingMode()) {
  const stepByKey = Object.fromEntries(allOnboardingSteps.map((step) => [step.key, step])) as Record<OnboardingStepKey, OnboardingStep>

  return stepsByMode[mode].map((key) => {
    const step = stepByKey[key]
    return {
      ...step,
      label: labelsByMode[mode]?.[key] ?? step.label,
      subtitle: subtitlesByMode[mode]?.[key] ?? step.subtitle,
    }
  })
}

export function getStepIndex(stepKey: OnboardingStepKey, mode = getCurrentOnboardingMode()) {
  return getOnboardingSteps(mode).findIndex((step) => step.key === stepKey)
}

export function getStep(stepKey: OnboardingStepKey, mode = getCurrentOnboardingMode()) {
  return getOnboardingSteps(mode).find((step) => step.key === stepKey) ?? null
}

export function getPreviousStepPath(stepKey: OnboardingStepKey, mode = getCurrentOnboardingMode()) {
  const steps = getOnboardingSteps(mode)
  const index = steps.findIndex((step) => step.key === stepKey)
  if (index <= 0) {
    return null
  }

  return steps[index - 1]?.path ?? null
}

export function getNextStepPath(stepKey: OnboardingStepKey, mode = getCurrentOnboardingMode()) {
  const steps = getOnboardingSteps(mode)
  const index = steps.findIndex((step) => step.key === stepKey)
  if (index < 0 || index >= steps.length - 1) {
    return null
  }

  return steps[index + 1]?.path ?? null
}